/**
 * RecurringEntriesManager - Manage scheduled recurring journal entries
 */

import React, { useState, useEffect } from 'react';
import {
  Account,
  RecurringEntryTemplate,
  RecurringTemplateCreateRequest,
  RecurringEntryLine
} from '../../../types/accounting';
import { accountingApi } from '../../../services/accountingApi';

interface RecurringEntriesManagerProps {
  businessId: number;
  accounts: Record<string, Account[]>;
  currency: 'CAD' | 'USD';
  onClose: () => void;
}

type ViewMode = 'list' | 'create' | 'edit';

const RecurringEntriesManager: React.FC<RecurringEntriesManagerProps> = ({
  businessId,
  accounts,
  currency,
  onClose
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [templates, setTemplates] = useState<RecurringEntryTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<RecurringEntryTemplate | null>(null);

  // Form state
  const [formData, setFormData] = useState<RecurringTemplateCreateRequest>({
    template_name: '',
    description: '',
    entry_type: 'general',
    lines: [
      { account_id: 0, description: '', debit_amount: 0, credit_amount: 0 },
      { account_id: 0, description: '', debit_amount: 0, credit_amount: 0 }
    ],
    frequency: 'monthly',
    interval: 1,
    day_of_month: 1,
    start_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadTemplates();
  }, [businessId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await accountingApi.getRecurringTemplates(businessId);
      if (response.status === 'success' && response.data?.templates) {
        setTemplates(response.data.templates);
      }
    } catch (err) {
      setError('Failed to load recurring entries');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    // Validate
    if (!formData.template_name) {
      setError('Template name is required');
      return;
    }

    const validLines = formData.lines.filter(l => l.account_id > 0 && (l.debit_amount > 0 || l.credit_amount > 0));
    if (validLines.length < 2) {
      setError('At least two entry lines are required');
      return;
    }

    const totalDebits = validLines.reduce((sum, l) => sum + (l.debit_amount || 0), 0);
    const totalCredits = validLines.reduce((sum, l) => sum + (l.credit_amount || 0), 0);
    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      setError('Debits must equal credits');
      return;
    }

    try {
      setLoading(true);
      const response = await accountingApi.createRecurringTemplate(businessId, {
        ...formData,
        lines: validLines
      });

      if (response.status === 'success') {
        resetForm();
        setViewMode('list');
        loadTemplates();
      } else {
        setError(response.message || 'Failed to create template');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (templateId: number) => {
    try {
      await accountingApi.pauseRecurringTemplate(templateId);
      loadTemplates();
    } catch (err) {
      setError('Failed to pause template');
    }
  };

  const handleResume = async (templateId: number) => {
    try {
      await accountingApi.resumeRecurringTemplate(templateId);
      loadTemplates();
    } catch (err) {
      setError('Failed to resume template');
    }
  };

  const handleSkip = async (templateId: number) => {
    try {
      await accountingApi.skipRecurringOccurrence(templateId);
      loadTemplates();
    } catch (err) {
      setError('Failed to skip occurrence');
    }
  };

  const handleDelete = async (templateId: number) => {
    if (!window.confirm('Are you sure you want to delete this recurring entry?')) {
      return;
    }

    try {
      await accountingApi.deleteRecurringTemplate(templateId);
      loadTemplates();
    } catch (err) {
      setError('Failed to delete template');
    }
  };

  const resetForm = () => {
    setFormData({
      template_name: '',
      description: '',
      entry_type: 'general',
      lines: [
        { account_id: 0, description: '', debit_amount: 0, credit_amount: 0 },
        { account_id: 0, description: '', debit_amount: 0, credit_amount: 0 }
      ],
      frequency: 'monthly',
      interval: 1,
      day_of_month: 1,
      start_date: new Date().toISOString().split('T')[0]
    });
    setEditingTemplate(null);
    setError(null);
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { account_id: 0, description: '', debit_amount: 0, credit_amount: 0 }]
    });
  };

  const removeLine = (index: number) => {
    if (formData.lines.length > 2) {
      setFormData({
        ...formData,
        lines: formData.lines.filter((_, i) => i !== index)
      });
    }
  };

  const updateLine = (index: number, field: keyof RecurringEntryLine, value: number | string) => {
    const newLines = [...formData.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setFormData({ ...formData, lines: newLines });
  };

  // Get all accounts flat
  const allAccounts = Object.values(accounts).flat();

  const locale = currency === 'CAD' ? 'en-CA' : 'en-US';
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
  };

  const formatFrequency = (template: RecurringEntryTemplate) => {
    const { frequency, interval, day_of_week, day_of_month } = template;
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    if (frequency === 'daily') {
      return interval === 1 ? 'Daily' : `Every ${interval} days`;
    } else if (frequency === 'weekly') {
      const day = day_of_week !== undefined ? days[day_of_week] : 'Monday';
      return interval === 1 ? `Every ${day}` : `Every ${interval} weeks on ${day}`;
    } else if (frequency === 'monthly') {
      const dayStr = day_of_month === 1 ? '1st' : day_of_month === 2 ? '2nd' : day_of_month === 3 ? '3rd' : `${day_of_month}th`;
      return interval === 1 ? `Monthly on the ${dayStr}` : `Every ${interval} months on the ${dayStr}`;
    } else if (frequency === 'yearly') {
      return 'Yearly';
    }
    return frequency;
  };

  // Calculate totals for form
  const formTotals = {
    debits: formData.lines.reduce((sum, l) => sum + (l.debit_amount || 0), 0),
    credits: formData.lines.reduce((sum, l) => sum + (l.credit_amount || 0), 0)
  };
  const isBalanced = Math.abs(formTotals.debits - formTotals.credits) < 0.01 && formTotals.debits > 0;

  return (
    <div style={{ padding: '20px', maxWidth: '900px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>
          {viewMode === 'list' ? 'Recurring Entries' : viewMode === 'create' ? 'New Recurring Entry' : 'Edit Recurring Entry'}
        </h3>
        <div>
          {viewMode === 'list' ? (
            <button
              onClick={() => { resetForm(); setViewMode('create'); }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '8px'
              }}
            >
              + New Recurring Entry
            </button>
          ) : (
            <button
              onClick={() => { resetForm(); setViewMode('list'); }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '8px'
              }}
            >
              Back to List
            </button>
          )}
          <button onClick={onClose} style={{ padding: '8px 16px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}>x</button>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div>
          {loading ? (
            <p>Loading...</p>
          ) : templates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              <p style={{ color: '#6b7280', marginBottom: '16px' }}>No recurring entries set up yet.</p>
              <p style={{ color: '#9ca3af', fontSize: '14px' }}>Create recurring entries for rent, payroll, monthly fees, etc.</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Frequency</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Next Run</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(template => (
                  <tr key={template.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: 500 }}>{template.template_name}</div>
                      {template.description && <div style={{ fontSize: '12px', color: '#6b7280' }}>{template.description}</div>}
                    </td>
                    <td style={{ padding: '12px', color: '#6b7280' }}>{formatFrequency(template)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {formatCurrency(template.lines.reduce((sum, l) => sum + (l.debit_amount || 0), 0))}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {template.next_run_date ? new Date(template.next_run_date).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        backgroundColor: template.status === 'active' ? '#d1fae5' : template.status === 'paused' ? '#fef3c7' : '#e5e7eb',
                        color: template.status === 'active' ? '#059669' : template.status === 'paused' ? '#d97706' : '#6b7280'
                      }}>
                        {template.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {template.status === 'active' ? (
                        <>
                          <button onClick={() => handleSkip(template.id)} style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Skip</button>
                          <button onClick={() => handlePause(template.id)} style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px', backgroundColor: '#fef3c7', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Pause</button>
                        </>
                      ) : template.status === 'paused' ? (
                        <button onClick={() => handleResume(template.id)} style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px', backgroundColor: '#d1fae5', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Resume</button>
                      ) : null}
                      <button onClick={() => handleDelete(template.id)} style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#fee2e2', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#dc2626' }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Create/Edit Form */}
      {(viewMode === 'create' || viewMode === 'edit') && (
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          {/* Template Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Template Name</label>
            <input
              type="text"
              placeholder="e.g., Monthly Rent, Weekly Payroll"
              value={formData.template_name}
              onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Description <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
            <input
              type="text"
              placeholder="Brief description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          {/* Schedule */}
          <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontWeight: 500 }}>Schedule</label>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Frequency</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              {formData.frequency === 'weekly' && (
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Day of Week</label>
                  <select
                    value={formData.day_of_week ?? 0}
                    onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  >
                    <option value={0}>Monday</option>
                    <option value={1}>Tuesday</option>
                    <option value={2}>Wednesday</option>
                    <option value={3}>Thursday</option>
                    <option value={4}>Friday</option>
                    <option value={5}>Saturday</option>
                    <option value={6}>Sunday</option>
                  </select>
                </div>
              )}

              {(formData.frequency === 'monthly' || formData.frequency === 'yearly') && (
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Day of Month</label>
                  <select
                    value={formData.day_of_month ?? 1}
                    onChange={(e) => setFormData({ ...formData, day_of_month: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  >
                    {Array.from({ length: 31 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>End Date <span style={{ color: '#9ca3af' }}>(optional)</span></label>
                <input
                  type="date"
                  value={formData.end_date || ''}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value || undefined })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Max Occurrences <span style={{ color: '#9ca3af' }}>(optional)</span></label>
                <input
                  type="number"
                  placeholder="Unlimited"
                  value={formData.max_occurrences || ''}
                  onChange={(e) => setFormData({ ...formData, max_occurrences: e.target.value ? parseInt(e.target.value) : undefined })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Journal Entry Lines */}
          <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontWeight: 500 }}>Journal Entry Lines</label>

            {formData.lines.map((line, index) => (
              <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 100px 100px 30px', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                <select
                  value={line.account_id || ''}
                  onChange={(e) => updateLine(index, 'account_id', parseInt(e.target.value) || 0)}
                  style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                >
                  <option value="">Select account...</option>
                  {allAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.account_number} - {account.account_name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="Debit"
                  value={line.debit_amount || ''}
                  onChange={(e) => updateLine(index, 'debit_amount', parseFloat(e.target.value) || 0)}
                  style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'right' }}
                  step="0.01"
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Credit"
                  value={line.credit_amount || ''}
                  onChange={(e) => updateLine(index, 'credit_amount', parseFloat(e.target.value) || 0)}
                  style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'right' }}
                  step="0.01"
                  min="0"
                />
                {formData.lines.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeLine(index)}
                    style={{ padding: '4px 8px', backgroundColor: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '18px' }}
                  >
                    x
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addLine}
              style={{ marginTop: '8px', padding: '6px 12px', backgroundColor: 'transparent', border: '1px dashed #9ca3af', borderRadius: '4px', color: '#6b7280', cursor: 'pointer', fontSize: '13px' }}
            >
              + Add Line
            </button>

            {/* Totals */}
            <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '24px' }}>
              <div>
                <span style={{ color: '#6b7280', fontSize: '13px' }}>Total Debits: </span>
                <span style={{ fontFamily: 'monospace' }}>{formatCurrency(formTotals.debits)}</span>
              </div>
              <div>
                <span style={{ color: '#6b7280', fontSize: '13px' }}>Total Credits: </span>
                <span style={{ fontFamily: 'monospace' }}>{formatCurrency(formTotals.credits)}</span>
              </div>
              <div>
                <span style={{ color: isBalanced ? '#059669' : '#dc2626', fontWeight: 500 }}>
                  {isBalanced ? 'Balanced' : 'Not Balanced'}
                </span>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => { resetForm(); setViewMode('list'); }}
              style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={loading || !isBalanced}
              style={{
                padding: '10px 20px',
                backgroundColor: isBalanced ? '#059669' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isBalanced ? 'pointer' : 'not-allowed',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving...' : 'Create Recurring Entry'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurringEntriesManager;
